import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { api } from '../../api/client';
import { Card } from '../../components/card';
import { Symbol } from '../../components/symbol';
import { SkeletonRows } from '../../components/skeleton';
import { Toggle } from '../../components/toggle';
import { EmptyState } from '../../components/empty-state';
import { TableSearch, useTableSearch } from '../../components/table-search';
import { useConfirm } from '../../components/confirm-dialog';
import { useAuthStore } from '../../store/auth-store';
import { mediaSrc } from '../../api/media';
import {
  CrudDrawer,
  TextField,
  NumberField,
  SelectField,
  CheckField,
  DeleteButton,
  extractErr,
} from '../../components/crud-drawer';

// GET /admin/categories returns the whole tree flat, ordered by (parentId,
// sortOrder). Two levels are rendered — a third would need indentation the
// customer app's category strip cannot show anyway.

export interface Category {
  id: string;
  parentId: string | null;
  name: string;
  slug: string;
  iconUrl: string | null;
  imageUrl: string | null;
  sortOrder: number;
  isActive: boolean;
  openBoxDefault: boolean;
  /** Who created it; null for categories that predate the record. */
  createdBy?: { type: 'ADMIN' | 'SELLER'; id: string; name: string | null } | null;
}

/** "Seller · Lakshmi Silks" / "Admin · Prasad" / "—". */
function creatorLabel(c: Category): string {
  if (!c.createdBy) return '—';
  const who = c.createdBy.name ?? `#${c.createdBy.id}`;
  return `${c.createdBy.type === 'SELLER' ? 'Seller' : 'Admin'} · ${who}`;
}

/** Slugify a display name so an admin never has to think about the URL form. */
function slugify(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

const EMPTY = {
  name: '',
  slug: '',
  parentId: '',
  iconUrl: '',
  imageUrl: '',
  sortOrder: '',
  isActive: true,
  openBoxDefault: false,
};

export function CategoriesTab() {
  const canWrite = useAuthStore((s) => s.hasScope)('catalog.category.write');

  const [rows, setRows] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<Category | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const { confirm } = useConfirm();
  // Client-side: the API returns the whole tree. Matches each column shown,
  // plus the parent's name so "sarees" also lists every sub-category under it.
  const parentName = (r: Category) => rows.find((p) => p.id === r.parentId)?.name;
  const { query, setQuery, filtered } = useTableSearch(rows, (r) => [
    r.name, r.slug, r.sortOrder, r.isActive ? 'Active' : 'Inactive', parentName(r), creatorLabel(r),
  ]);
  /** Whether the slug still tracks the name — stops an auto-slug overwriting a
   *  slug the admin deliberately typed. */
  const [slugTouched, setSlugTouched] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get<Category[]>('/admin/categories');
      setRows(data);
    } catch (err) {
      toast.error(extractErr(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  const roots = useMemo(() => rows.filter((r) => r.parentId === null), [rows]);
  // While searching, keep the tree shape: a root shows when it or any of its
  // children match, and only matching children show beneath it.
  const matched = useMemo(() => new Set(filtered.map((r) => r.id)), [filtered]);
  const visibleRoots = useMemo(
    () =>
      roots.filter(
        (r) => matched.has(r.id) || rows.some((c) => c.parentId === r.id && matched.has(c.id)),
      ),
    [roots, rows, matched],
  );
  const childrenOf = useMemo(() => {
    const map = new Map<string, Category[]>();
    for (const r of rows) {
      if (r.parentId === null) continue;
      map.set(r.parentId, [...(map.get(r.parentId) ?? []), r]);
    }
    return map;
  }, [rows]);

  const openCreate = (parentId?: string) => {
    setEditing(null);
    setCreating(true);
    setSlugTouched(false);
    setForm({ ...EMPTY, parentId: parentId ?? '' });
  };

  const openEdit = (row: Category) => {
    setCreating(false);
    setEditing(row);
    setSlugTouched(true);
    setForm({
      name: row.name,
      slug: row.slug,
      parentId: row.parentId ?? '',
      iconUrl: row.iconUrl ?? '',
      imageUrl: row.imageUrl ?? '',
      sortOrder: String(row.sortOrder),
      isActive: row.isActive,
      openBoxDefault: row.openBoxDefault,
    });
  };

  const close = () => { setEditing(null); setCreating(false); };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    // Same rule as the API (which has the final say): no two categories with
    // the same name, ignoring case and spacing, under the same parent.
    const norm = (s: string) => s.trim().replace(/\s+/g, ' ').toLowerCase();
    const clash = rows.find(
      (r) => r.id !== editing?.id && (r.parentId ?? '') === form.parentId && norm(r.name) === norm(form.name),
    );
    if (clash) {
      toast.error(`"${clash.name}" already exists${form.parentId ? ' under this parent' : ''}.`);
      return;
    }
    const slug = (form.slug || slugify(form.name)).trim();
    if (!/^[a-z0-9-]+$/.test(slug)) {
      toast.error('Slug may only contain lowercase letters, numbers and hyphens');
      return;
    }
    const body = {
      name: form.name.trim(),
      slug,
      parentId: form.parentId || null,
      iconUrl: form.iconUrl.trim() || null,
      imageUrl: form.imageUrl.trim() || null,
      ...(form.sortOrder !== '' ? { sortOrder: Number(form.sortOrder) } : {}),
      isActive: form.isActive,
    };
    setBusy(true);
    try {
      if (creating) {
        await api.post('/admin/categories', body);
        toast.success('Category created');
      } else if (editing) {
        await api.put(`/admin/categories/${editing.id}`, body);
        toast.success('Category updated');
      }
      close();
      await load();
    } catch (err) {
      toast.error(extractErr(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!editing) return;
    const kids = childrenOf.get(editing.id)?.length ?? 0;
    if (kids > 0) {
      toast.error(`"${editing.name}" has ${kids} sub-categor${kids === 1 ? 'y' : 'ies'}. Move or delete those first.`);
      return;
    }
    const ok = await confirm({
      title: `Delete "${editing.name}"?`,
      description: 'This can\'t be undone, and it fails if any product still uses the category. Deactivate it instead to hide it from shoppers.',
      confirmLabel: 'Delete category',
      tone: 'danger',
    });
    if (!ok) return;
    setBusy(true);
    try {
      await api.delete(`/admin/categories/${editing.id}`);
      toast.success('Category deleted');
      close();
      await load();
    } catch (err) {
      toast.error(extractErr(err));
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (row: Category, next: boolean) => {
    // Switching off hides it at once, so ask first; switching back on is
    // harmless and stays one click.
    if (!next) {
      const ok = await confirm({
        title: `Deactivate "${row.name}"?`,
        description: 'It is hidden from shoppers and from the seller\'s product form. You can switch it back on any time.',
        confirmLabel: 'Deactivate category',
        tone: 'danger',
      });
      if (!ok) return;
    }
    setRows((cur) => cur.map((r) => (r.id === row.id ? { ...r, isActive: next } : r)));
    try {
      await api.put(`/admin/categories/${row.id}`, { isActive: next });
    } catch (err) {
      toast.error(extractErr(err));
      setRows((cur) => cur.map((r) => (r.id === row.id ? { ...r, isActive: !next } : r)));
    }
  };

  // A category cannot be its own parent, nor a child of its own descendant.
  const parentOptions = useMemo(
    () =>
      roots
        .filter((r) => r.id !== editing?.id)
        .map((r) => ({ value: r.id, label: r.name })),
    [roots, editing],
  );

  const renderRow = (row: Category, depth: number) => (
    <tr key={row.id} className={row.isActive ? '' : 'bg-surface-sunk'}>
      <td>
        <div className="flex items-center gap-2" style={{ paddingLeft: depth * 20 }}>
          {depth > 0 && <Symbol name="subdirectory_arrow_right" size={14} className="text-ink-300" />}
          {row.iconUrl ? (
            <img src={mediaSrc(row.iconUrl)} alt="" className="h-6 w-6 rounded-xs object-cover" />
          ) : (
            <div className="grid h-6 w-6 place-items-center rounded-xs bg-surface-2">
              <Symbol name="category" size={14} className="text-ink-400" />
            </div>
          )}
          <span className="font-medium text-ink-900">{row.name}</span>
        </div>
      </td>
      <td className="sku text-ink-500">{row.slug}</td>
      <td className="hidden text-[13px] text-ink-700 md:table-cell">
        {row.createdBy?.type === 'SELLER' ? (
          <span className="badge badge-warning">{creatorLabel(row)}</span>
        ) : (
          creatorLabel(row)
        )}
      </td>
      <td className="hidden text-right sku text-ink-500 sm:table-cell">{row.sortOrder}</td>
      <td>
        <div className="flex justify-end">
          <Toggle
            checked={row.isActive}
            disabled={!canWrite}
            aria-label={`${row.isActive ? 'Deactivate' : 'Activate'} ${row.name}`}
            onChange={(v) => void toggleActive(row, v)}
          />
        </div>
      </td>
      <td className="text-right">
        {canWrite && (
          <div className="flex justify-end gap-1">
            {depth === 0 && (
              <button
                type="button"
                onClick={() => openCreate(row.id)}
                title="Add a sub-category"
                className="btn btn-quiet btn-sm"
              >
                <Symbol name="add" size={16} /> Sub
              </button>
            )}
            <button
              type="button"
              onClick={() => openEdit(row)}
              className="btn btn-ghost btn-sm"
            >
              Edit
            </button>
          </div>
        )}
      </td>
    </tr>
  );

  return (
    <>
      <Card padding="none">
        <div className="flex items-center justify-between border-b border-surface-line px-5 py-4">
          <div>
            <h2 className="text-[16px] font-semibold text-ink-900">Categories</h2>
            <p className="mt-0.5 text-[13.5px] text-ink-500">
              Two levels. Every product belongs to exactly one — sellers pick from this list, and can add one when nothing fits (marked “Seller” below).
            </p>
          </div>
          {canWrite && (
            <button
              type="button"
              onClick={() => openCreate()}
              className="btn btn-primary btn-sm shrink-0"
            >
              <Symbol name="add" size={16} /> Add category
            </button>
          )}
        </div>

        {!loading && rows.length > 0 && (
          <div className="border-b border-surface-line px-5 py-3">
            <TableSearch
              value={query}
              onChange={setQuery}
              placeholder="Search by name, parent, slug, creator, order or status"
              resultCount={filtered.length}
            />
          </div>
        )}

        {loading ? (
          <SkeletonRows cols={5} />
        ) : rows.length === 0 ? (
          <EmptyState
            bare
            icon="category"
            title="No categories yet"
            body="Sellers cannot list a product until at least one exists."
          />
        ) : visibleRoots.length === 0 ? (
          <EmptyState
            bare
            icon="search_off"
            title={`No matches for "${query.trim()}"`}
            body="Try a category or parent name, its slug, a sort number, or Active / Inactive."
            action={
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => setQuery('')}>
                Clear search
              </button>
            }
          />
        ) : (
          <div className="overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Slug</th>
                <th className="hidden md:table-cell">Created by</th>
                <th className="hidden !text-right sm:table-cell">Order</th>
                <th className="!text-right">Active</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {visibleRoots.flatMap((root) => [
                renderRow(root, 0),
                ...(childrenOf.get(root.id) ?? [])
                  .filter((child) => matched.has(child.id))
                  .map((child) => renderRow(child, 1)),
              ])}
            </tbody>
          </table>
          </div>
        )}
      </Card>

      <CrudDrawer
        open={creating || editing !== null}
        title={creating ? 'New category' : `Edit ${editing?.name ?? ''}`}
        busy={busy}
        onClose={close}
        onSubmit={() => void save()}
        secondaryAction={editing ? <DeleteButton onClick={() => void remove()} disabled={busy} /> : undefined}
      >
        <TextField
          label="Name"
          value={form.name}
          onChange={(v) =>
            setForm((f) => ({ ...f, name: v, slug: slugTouched ? f.slug : slugify(v) }))
          }
          placeholder="Sarees"
          required
        />
        <TextField
          label="Slug"
          value={form.slug}
          onChange={(v) => { setSlugTouched(true); setForm((f) => ({ ...f, slug: v })); }}
          placeholder="sarees"
          mono
          required
          hint="Used in customer-facing URLs. Changing it on a live category breaks existing links."
        />
        <SelectField
          label="Parent category"
          value={form.parentId}
          onChange={(v) => setForm((f) => ({ ...f, parentId: v }))}
          options={parentOptions}
          placeholder="— Top level —"
          hint="Leave empty for a top-level category."
        />
        <TextField
          label="Icon URL"
          value={form.iconUrl}
          onChange={(v) => setForm((f) => ({ ...f, iconUrl: v }))}
          placeholder="https://…"
          hint="Small square icon for the home-screen category strip."
        />
        <TextField
          label="Image URL"
          value={form.imageUrl}
          onChange={(v) => setForm((f) => ({ ...f, imageUrl: v }))}
          placeholder="https://…"
          hint="Wide banner shown at the top of the category listing."
        />
        <NumberField
          label="Sort order"
          value={form.sortOrder}
          onChange={(v) => setForm((f) => ({ ...f, sortOrder: v }))}
          placeholder="0"
          min={0}
          hint="Lower numbers appear first."
        />
        <CheckField
          label="Active"
          hint="Inactive categories are hidden from shoppers and from the seller's product form."
          checked={form.isActive}
          onChange={(v) => setForm((f) => ({ ...f, isActive: v }))}
        />
      </CrudDrawer>
    </>
  );
}
